<template>
  <v-ace-editor v-if="editorLang" v-model:value="sourceCode"
                class="font-mono bg-white mt-14 text-base min-h-80 rounded-lg md:border border-gray-200 md:p-10"
                :lang="editorLang"
                :readonly="read==='true'"
                @change="sendCode"
                :style="read==='true'?`min-height: calc(1.52rem * ${lines})`:''"
                theme="xcode"/>
  <div v-if="errorResult" class="font-mono flex-col justify-start w-full text-sm mt-3 text-slate-700 font-medium
   rounded-lg p-2.5 bg-red-300 border border-red-400 ">
    <div class="text-base" v-html="t('coderunner.resultType.error')"/>
    <div class="text-sm" v-html="errorResult"/>
  </div>
  <div v-if="successResult && !isQuiz" class="font-mono flex-col justify-start w-full text-sm mt-3 text-slate-700 font-medium
   rounded-lg p-2.5 bg-green-300 border border-green-400 ">
    <div class="text-base" v-html="t('coderunner.resultType.success')"/>
    <div class="text-sm" v-html="successResult"/>
  </div>
  <btn-wrapper v-if="!isQuiz && read==='false' && language!=='markup'" :params="{
                      class: 'bg-default',
                      type: 'button',
                      title: button!=='undefined'?button:t('coderunner.button.run'),
                  }"
               @click="runCode()"
               :loading="localLoading"
  />
  <div class="flex flex-row gap-2" v-else-if="isQuiz && !disabled">
    <btn-wrapper :params="{
                        class: 'bg-default',
                        type: 'button',
                        title: t('coderunner.button.check'),
                    }"
                 @click="sendAndRunCode(true)"
                 :loading="loading || localLoading"
                 v-if="precheck"
    />
    <btn-wrapper :params="{
                        class: 'bg-default',
                        type: 'button',
                        title: t('coderunner.button.send'),
                    }"
                 @click="sendAndRunCode(false)"
                 :loading="loading || localLoading"
    />
  </div>
</template>

<script>
import '@/script/ace-config';
import {mapActions} from "vuex";
import {useToast} from "vue-toastification";
import {Base64} from "js-base64";

const toast = useToast()

export default {
  name: 'Coderunner',
  props: {
    code: String,
    language: String,
    stdin: String,
    files: String,
    button: String,
    mode: String,
    id: String,
    read: {
      type: String,
      default: 'false'
    },
    isQuiz: Boolean,
    loading: Boolean,
    precheck: Boolean,
    disabled: {
      type: Boolean,
      default: false
    }
  },

  emits: ['saveData', 'setCheck', 'runData'],
  methods: {
    ...mapActions(['getContextId', 'runCodeInSandbox']),
    sendCode() {
      if (this.isQuiz) {
        this.$emit('saveData', this.sourceCode)
      }
    },

    sendAndRunCode(precheck = false) {
        this.sendCode()
        this.runCode(() => {this.$emit('runData', {code: this.sourceCode, precheck: precheck})}, this.stdin)
    },

    setResult(result, callback = null) {
      if (result && JSON.parse(result).output) {
        this.successResult = JSON.parse(result).output
      } else if (result && JSON.parse(result).stderr && !this.isQuiz) {
        this.errorResult = JSON.parse(result).stderr
      } else if (result && JSON.parse(result).cmpinfo) {
        this.errorResult = JSON.parse(result).cmpinfo
      }

      if (callback && !JSON.parse(result).cmpinfo) {
          callback()
      }

      this.successResult = this.successResult.replaceAll(/(\n)/g, '<br>')
      this.successResult = this.successResult.replaceAll(/(\s)/g, '&nbsp;')
      this.successResult = this.successResult.replaceAll(/(\r)/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
      this.errorResult = this.errorResult.replaceAll(/(\n)/g, '<br>')
      this.errorResult = this.errorResult.replaceAll(/(\s)/g, '&nbsp;')
      this.errorResult = this.errorResult.replaceAll(/(\r)/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
    },

    runCode(callback = null, stdin = null) {
      this.successResult = ''
      this.errorResult = ''

      if (stdin == null && !this.isQuiz) {
        if (this.stdin !== 'null' && this.stdin !== 'undefined' && this.stdin && document.getElementById(this.stdin)) {
          stdin = document.getElementById(this.stdin).value;
        }
      }

      if (this.isQuiz && !this.stdin) {
        if (callback) callback()

        return
      }

      const files = {};

      if (this.files !== 'null' && this.files !== 'undefined' && this.files) {
        Object.keys(JSON.parse(this.files)).forEach(key => {
          files[key] = document.getElementById(JSON.parse(this.files)[key]).value;
        })
      }

      const data = {
        sourcecode: this.sourceCode,
        language: this.langReplace[this.language],
        params: JSON.stringify({cputime: 5, runargs: []}),
        stdin: stdin,
        files: JSON.stringify(files),
        contextid: this.contextId,
      }
      this.localLoading = true

      this.runCodeInSandbox(data).then(result => {
        this.localLoading = false

        this.setResult(result.data, callback)
      }).catch(error => {
        this.localLoading = false
        console.error(error);
        toast.error(this.$t('coderunner.errorRunCode'));
      })
    }
  },

  data: () => ({
    langReplace: {
      'python': 'python3',
      'javascript': 'nodejs',
      'css': 'css',
      'cpp': 'cpp',
      'csharp': 'Csharp',
      'c': 'c',
      'ruby': 'ruby',
      'php': 'php',
      'java': 'java',
    },
    sourceCode: '',
    contextId: 1,
    errorResult: '',
    successResult: '',
    localLoading: false,
    editorLang: 'python',
    lines: 5,
  }),

  created() {
    if (this.language === 'c' || this.language === 'cpp') {
      this.editorLang = 'c_cpp'
    } else if (this.language === 'markup' || this.language === '') {
      this.editorLang = 'html'
    } else {
      this.editorLang = this.language
    }

    this.getContextId({mode: this.mode, id: this.id}).then((contextId) => {
      this.contextId = contextId;

      if (this.code) {
        this.sourceCode = Base64.decode(this.code);
        this.lines = this.sourceCode.split('\n').length
      }
    }).catch((err) => {
      console.log(err);
    })
  }
}
</script>

<script setup>
import {useI18n} from "vue-i18n";

const {t} = useI18n()
</script>

<style lang="scss" scoped>

@font-face {
  font-family: "JetBrains Mono";
  src: url('./fonts/JetBrainsMono-Medium.woff2');
}

@font-face {
  font-family: "JetBrains Mono Bold";
  src: url('./fonts/JetBrainsMono-Bold.woff2');
}

.ace-chrome .ace_gutter {
  background: #ebebeb;
  color: #333;
  overflow: hidden;
}

.ace-chrome .ace_print-margin {
  width: 1px;
  background: #e8e8e8;
}

.ace-chrome {
  background-color: #FFFFFF;
  color: black;
}

.ace-chrome .ace_cursor {
  color: black;
}

.ace-chrome .ace_invisible {
  color: rgb(191, 191, 191);
}

.ace-chrome .ace_constant.ace_buildin {
  color: rgb(88, 72, 246);
}

.ace-chrome .ace_constant.ace_language {
  color: rgb(88, 92, 246);
}

.ace-chrome .ace_constant.ace_library {
  color: rgb(6, 150, 14);
}

.ace-chrome .ace_invalid {
  background-color: rgb(153, 0, 0);
  color: white;
}

.ace-chrome .ace_fold {
}

.ace-chrome .ace_support.ace_function {
  color: rgb(60, 76, 114);
}

.ace-chrome .ace_support.ace_constant {
  color: rgb(6, 150, 14);
}

.ace-chrome .ace_support.ace_type,
.ace-chrome .ace_support.ace_class
.ace-chrome .ace_support.ace_other {
  color: rgb(109, 121, 222);
}

.ace-chrome .ace_variable.ace_parameter {
  font-style: italic;
  color: #FD971F;
}

.ace-chrome .ace_keyword.ace_operator {
  color: rgb(104, 118, 135);
}

.ace-chrome .ace_comment {
  color: #236e24;
}

.ace-chrome .ace_comment.ace_doc {
  color: #236e24;
}

.ace-chrome .ace_comment.ace_doc.ace_tag {
  color: #236e24;
}

.ace-chrome .ace_constant.ace_numeric {
  color: rgb(0, 0, 205);
}

.ace-chrome .ace_variable {
  color: rgb(49, 132, 149);
}

.ace-chrome .ace_xml-pe {
  color: rgb(104, 104, 91);
}

.ace-chrome .ace_entity.ace_name.ace_function {
  color: #0000A2;
}


.ace-chrome .ace_heading {
  color: rgb(12, 7, 255);
}

.ace-chrome .ace_list {
  color: rgb(185, 6, 144);
}

.ace-chrome .ace_marker-layer .ace_selection {
  background: rgb(181, 213, 255);
}

.ace-chrome .ace_marker-layer .ace_step {
  background: rgb(252, 255, 0);
}

.ace-chrome .ace_marker-layer .ace_stack {
  background: rgb(164, 229, 101);
}

.ace-chrome .ace_marker-layer .ace_bracket {
  margin: -1px 0 0 -1px;
  border: 1px solid rgb(192, 192, 192);
}

.ace-chrome .ace_marker-layer .ace_active-line {
  background: rgba(0, 0, 0, 0.07);
}

.ace-chrome .ace_gutter-active-line {
  background-color: #dcdcdc;
}

.ace-chrome .ace_marker-layer .ace_selected-word {
  background: rgb(250, 250, 255);
  border: 1px solid rgb(200, 200, 250);
}

.ace-chrome .ace_storage,
.ace-chrome .ace_keyword,
.ace-chrome .ace_meta.ace_tag {
  color: rgb(147, 15, 128);
}

.ace-chrome .ace_string.ace_regex {
  color: rgb(255, 0, 0)
}

.ace-chrome .ace_string {
  color: #1A1AA6;
}

.ace-chrome .ace_entity.ace_other.ace_attribute-name {
  color: #994409;
}

.ace-chrome .ace_indent-guide {
  background: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAACCAYAAACZgbYnAAAAE0lEQVQImWP4////f4bLly//BwAmVgd1/w11/gAAAABJRU5ErkJggg==") right repeat-y;
}

.ace-chrome .ace_indent-guide-active {
  background: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAACCAYAAACZgbYnAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAIGNIUk0AAHolAACAgwAA+f8AAIDpAAB1MAAA6mAAADqYAAAXb5JfxUYAAAAZSURBVHjaYvj///9/hivKyv8BAAAA//8DACLqBhbvk+/eAAAAAElFTkSuQmCC") right repeat-y;
}

</style>
